import type { TicketFeeMode } from '@pulsify/shared'
import type { EventTicketSummary, TicketSettings } from '@pulsify/shared/dto'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  type TicketOrdersFilter,
  type TicketOrdersQuery,
  type TicketTypeInput,
  ticketsApi,
} from '~/entities/ticket/api/tickets-api'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { MINUTES } from '~/shared/lib/time-constants'

const LIVE_REFRESH_MS = 20 * 1000

export function useTicketSettings(businessId: string) {
  return useQuery({
    queryKey: queryKey(QueryKey.TICKET_SETTINGS, businessId),
    queryFn: () => ticketsApi.settings(businessId),
    enabled: !!businessId,
  })
}

function useStoreSettings(businessId: string) {
  const queryClient = useQueryClient()
  return (settings: TicketSettings) =>
    queryClient.setQueryData(queryKey(QueryKey.TICKET_SETTINGS, businessId), settings)
}

export function useSetTicketFeeMode(businessId: string) {
  const store = useStoreSettings(businessId)
  return useMutation({
    mutationFn: (feeMode: TicketFeeMode) => ticketsApi.setFeeMode(businessId, feeMode),
    onSuccess: store,
  })
}

export function useStartPayoutSetup(businessId: string) {
  return useMutation({
    mutationFn: (urls: { returnUrl: string; refreshUrl: string }) =>
      ticketsApi.onboardingLink(businessId, urls),
  })
}

export function useRefreshPayoutSetup(businessId: string) {
  const store = useStoreSettings(businessId)
  return useMutation({
    mutationFn: () => ticketsApi.refreshConnect(businessId),
    onSuccess: store,
  })
}

export function useTicketEarnings(
  businessId: string,
  hostProfileId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKey(QueryKey.TICKET_EARNINGS, businessId, hostProfileId ?? 'all'),
    queryFn: () => ticketsApi.earnings(businessId, hostProfileId),
    staleTime: MINUTES,
    enabled: enabled && !!businessId,
  })
}

export function useEventTicketTypes(businessId: string, eventId: string) {
  return useQuery({
    queryKey: queryKey(QueryKey.EVENT_TICKET_TYPES, eventId),
    queryFn: () => ticketsApi.types(businessId, eventId),
    enabled: !!businessId && !!eventId,
  })
}

export function useEventTicketSummary(
  businessId: string,
  eventId: string,
  options: { enabled?: boolean; refreshWhile?: (summary: EventTicketSummary) => boolean } = {},
) {
  return useQuery({
    queryKey: queryKey(QueryKey.EVENT_TICKET_SUMMARY, eventId),
    queryFn: () => ticketsApi.summary(businessId, eventId),
    enabled: (options.enabled ?? true) && !!businessId && !!eventId,
    refetchInterval: (query) =>
      query.state.data && options.refreshWhile?.(query.state.data) ? LIVE_REFRESH_MS : false,
  })
}

export function useEventTicketOrdersPage(
  businessId: string,
  eventId: string,
  query: TicketOrdersQuery,
  live: boolean,
) {
  return useQuery({
    queryKey: queryKey(QueryKey.EVENT_TICKET_ORDERS, eventId, query),
    queryFn: () => ticketsApi.ordersPage(businessId, eventId, query),
    enabled: !!businessId && !!eventId,
    placeholderData: keepPreviousData,
    refetchInterval: live ? LIVE_REFRESH_MS : false,
  })
}

export function useExportTicketOrders(businessId: string, eventId: string) {
  return useMutation({
    mutationFn: (filter: TicketOrdersFilter) =>
      ticketsApi.exportOrders(businessId, eventId, filter),
  })
}

function useRefreshTickets(businessId: string, eventId: string) {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.EVENT_TICKET_TYPES, eventId) })
    queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.EVENT_TICKET_ORDERS, eventId) })
    queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.EVENT_TICKET_SUMMARY, eventId) })
    queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.TICKET_SETTINGS, businessId) })
    queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.TICKET_EARNINGS, businessId) })
  }
}

export function useSaveTicketType(businessId: string, eventId: string) {
  const refresh = useRefreshTickets(businessId, eventId)
  return useMutation({
    mutationFn: ({ typeId, input }: { typeId: string | null; input: TicketTypeInput }) =>
      typeId
        ? ticketsApi.updateType(businessId, typeId, input)
        : ticketsApi.createType(businessId, eventId, input),
    onSuccess: refresh,
  })
}

export function useDeleteTicketType(businessId: string, eventId: string) {
  const refresh = useRefreshTickets(businessId, eventId)
  return useMutation({
    mutationFn: (typeId: string) => ticketsApi.deleteType(businessId, typeId),
    onSuccess: refresh,
  })
}

export function useRefundTicketOrder(businessId: string, eventId: string) {
  const refresh = useRefreshTickets(businessId, eventId)
  return useMutation({
    mutationFn: (orderId: string) => ticketsApi.refund(businessId, orderId),
    onSuccess: refresh,
  })
}

export function useVoidTicket(businessId: string, eventId: string) {
  const refresh = useRefreshTickets(businessId, eventId)
  return useMutation({
    mutationFn: (ticketId: string) => ticketsApi.voidTicket(businessId, ticketId),
    onSuccess: refresh,
  })
}
